import moment from "moment";

import type { ListingDatePrice } from "../services/date-prices.service";

import type { DateRateFormValues } from "./DateRateForm";
import type { DateRate } from "./DateRateTable";

const API_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Turns a saved form's picked days into one `{ date, hourly_rate }` per day,
 * sorted by date, the shape `POST .../calendar-pricing/dates` expects.
 */
const toListingDatePrices = ({
  dates,
  hourlyRate,
}: DateRateFormValues): Pick<ListingDatePrice, "date" | "hourly_rate">[] =>
  dates
    .map((date) => moment(date).format(API_DATE_FORMAT))
    .sort()
    .map((date) => ({ date, hourly_rate: Number(hourlyRate) }));

const isNextDay = (date: string, previousDate: string) =>
  moment(previousDate, API_DATE_FORMAT)
    .add(1, "day")
    .format(API_DATE_FORMAT) === date;

/**
 * Groups a listing's per-day prices into the table's rows: consecutive dates
 * with the same hourly rate share a row. Mirrors mobile's `groupDatePrices`.
 */
const toDateRates = (datePrices: ListingDatePrice[]): DateRate[] =>
  [...datePrices]
    .sort((a, b) => a.date.localeCompare(b.date))
    .reduce<{ dates: string[]; hourlyRate: number }[]>(
      (groups, { date, hourly_rate }) => {
        const lastGroup = groups[groups.length - 1];
        const previousDate = lastGroup?.dates[lastGroup.dates.length - 1];

        if (
          previousDate &&
          lastGroup.hourlyRate === hourly_rate &&
          isNextDay(date, previousDate)
        ) {
          lastGroup.dates.push(date);
        } else {
          groups.push({ dates: [date], hourlyRate: hourly_rate });
        }

        return groups;
      },
      [],
    )
    .map(({ dates, hourlyRate }) => ({
      id: dates[0],
      dates,
      startDate: moment(dates[0], API_DATE_FORMAT).toDate(),
      endDate: moment(dates[dates.length - 1], API_DATE_FORMAT).toDate(),
      hourlyRate,
    }));

export { toDateRates, toListingDatePrices };
